import React, { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlaneUp, faTriangleExclamation, faXmark } from '@fortawesome/free-solid-svg-icons';
import { useOffline } from '../../offline/OfflineContext';
import './OfflineBanner.css';

/**
 * Turn an API path into something a trader can read.
 *
 * Deliberately mechanical — it derives the words from the path itself rather
 * than mapping to a curated label list. A curated list would be prettier and
 * would go stale the first time someone adds an endpoint, at which point this
 * bar would start describing the wrong thing, which is the exact class of lie
 * the bar exists to prevent. Ugly and true beats pretty and drifting.
 */
export function describeUrl(url) {
  const path = String(url || '').split('?')[0];
  const parts = path.split('/').filter(Boolean).filter((p) => p !== 'api');
  if (!parts.length) return path || 'part of this screen';
  return parts
    .filter((p) => !/^\d+$/.test(p))
    .slice(0, 2)
    .join(' → ')
    .replace(/[-_]/g, ' ');
}

function formatStamp(ms) {
  if (!ms) return '';
  const d = new Date(ms);
  const time = d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  const date = d.toLocaleDateString([], { day: 'numeric', month: 'short' });
  return `${time} on ${date}`;
}

function formatAge(ms) {
  if (ms == null) return '';
  const mins = Math.floor(ms / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;
  const days = Math.floor(hours / 24);
  return `${days} day${days === 1 ? '' : 's'} ago`;
}

/**
 * Always-visible offline state.
 *
 * Deliberately loud and deliberately specific about *when* the data is from.
 * The realistic failure mode of offline mode is not a crash — it is someone
 * quoting a price or a credit limit that was true eleven hours ago. A subtle
 * chip in a corner does not carry that weight; a full-width bar with a
 * timestamp does.
 */
const OfflineBanner = () => {
  const {
    offline, pack, lastBlocked, clearBlocked, staleness,
    misses, missesDismissed, dismissMisses,
  } = useOffline();
  const [missesOpen, setMissesOpen] = useState(false);

  /* Auto-dismiss the blocked-write toast.
   *
   * It used to persist until manually closed, so it outlived the screen that
   * raised it and reappeared to be "about" whatever page you navigated to
   * next — which is exactly how it got mistaken for the Account Information
   * page misbehaving. A toast describes a moment; it should not outlive it. */
  useEffect(() => {
    if (!lastBlocked) return undefined;
    const timer = setTimeout(clearBlocked, 8000);
    return () => clearTimeout(timer);
  }, [lastBlocked, clearBlocked]);

  if (!offline) return null;

  return (
    <>
      <div className="hms-offline-banner" role="status" aria-live="polite">
        <FontAwesomeIcon icon={faPlaneUp} className="hms-offline-banner-icon" />
        <span className="hms-offline-banner-main">
          <strong>Offline &mdash; read-only.</strong>{' '}
          Showing your saved snapshot from {formatStamp(pack && pack.generatedAt)}
        </span>
        <span className="hms-offline-banner-age">{formatAge(staleness)}</span>
      </div>

      {/* The anti-lie bar.
       *
       * Whatever a component decided to render for a miss — an empty table, a
       * zero in a tab, "N/A" in a field — this says the data was not in the
       * pack. It sits above the page so no screen's error handling can drop it,
       * and it is scoped to the current screen so it always refers to what is
       * actually in front of you. */}
      {misses.length > 0 && !missesDismissed && (
        <div className="hms-offline-missbar" role="alert">
          <FontAwesomeIcon icon={faTriangleExclamation} className="hms-offline-missbar-icon" />
          <div className="hms-offline-missbar-body">
            <strong>
              {misses.length === 1
                ? 'Part of this screen isn’t in your saved copy.'
                : `${misses.length} parts of this screen aren’t in your saved copy.`}
            </strong>{' '}
            <span>
              Anything showing as empty or zero here may not be the real figure &mdash;
              check it online before acting on it.
            </span>
            <button
              type="button"
              className="hms-offline-missbar-toggle"
              onClick={() => setMissesOpen((v) => !v)}
              aria-expanded={missesOpen}
            >
              {missesOpen ? 'Hide details' : 'What’s missing?'}
            </button>
            {missesOpen && (
              <ul className="hms-offline-missbar-list">
                {misses.map((m) => (
                  <li key={m.url}>
                    <span className="hms-offline-missbar-what">{describeUrl(m.url)}</span>
                    <code>{m.url}</code>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <button
            type="button"
            className="hms-offline-missbar-close"
            onClick={dismissMisses}
            aria-label="Dismiss"
          >
            <FontAwesomeIcon icon={faXmark} />
          </button>
        </div>
      )}

      {lastBlocked && (
        <div className="hms-offline-toast" role="alert">
          <FontAwesomeIcon icon={faTriangleExclamation} className="hms-offline-toast-icon" />
          <div className="hms-offline-toast-body">
            <div className="hms-offline-toast-title">That change wasn&rsquo;t saved</div>
            <div className="hms-offline-toast-text">
              You&rsquo;re offline, so IMT is read-only. Reconnect and try again &mdash;
              nothing was sent.
            </div>
          </div>
          <button
            type="button"
            className="hms-offline-toast-close"
            onClick={clearBlocked}
            aria-label="Dismiss"
          >
            <FontAwesomeIcon icon={faXmark} />
          </button>
        </div>
      )}
    </>
  );
};

export default OfflineBanner;
